import {
  calculateDistance,
  getCentroidFromPolygonOrMultiPolygon,
} from './geometryUtils';

/** Missing `is_boundary` is a tract so older payloads stay property lines. */
export function isHuntTract(boundary) {
  if (!boundary) return false;
  return boundary.is_boundary !== false;
}

export function clubTracts(boundaries) {
  if (!Array.isArray(boundaries)) return [];
  return boundaries.filter(isHuntTract);
}

/** Club planting Ground lists fields only. Missing `is_boundary` is a tract. */
export function isHuntField(boundary) {
  if (!boundary) return false;
  return boundary.is_boundary === false;
}

export function clubFields(boundaries) {
  if (!Array.isArray(boundaries)) return [];
  return boundaries.filter(isHuntField);
}

export function clubLandDrawIsBoundary(tool) {
  return tool !== 'field';
}

export function clubLandDrawDefaultName(tool) {
  return tool === 'field' ? 'Field' : 'Boundary';
}

export function clubLandDrawHint(tool) {
  return tool === 'field'
    ? 'A field does not change club acreage.'
    : 'The property line counts toward acreage.';
}

export function clubLandSyncDefaultName(currentName, isBoundary) {
  const next = clubLandDrawDefaultName(isBoundary !== false ? 'boundary' : 'field');
  const current = String(currentName || '').trim();
  if (!current || current === 'Boundary' || current === 'Field') return next;
  return currentName;
}

function pointCoords(pointGeojson) {
  if (!pointGeojson) return null;
  if (pointGeojson.type === 'Feature') return pointCoords(pointGeojson.geometry);
  if (pointGeojson.type !== 'Point' || !Array.isArray(pointGeojson.coordinates)) return null;
  const [lng, lat] = pointGeojson.coordinates;
  if (typeof lng !== 'number' || typeof lat !== 'number') return null;
  return [lng, lat];
}

function ringContains(lng, lat, ring) {
  if (!Array.isArray(ring) || ring.length < 4) return false;
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    const intersects = ((yi > lat) !== (yj > lat))
      && (lng < ((xj - xi) * (lat - yi)) / ((yj - yi) || Number.EPSILON) + xi);
    if (intersects) inside = !inside;
  }
  return inside;
}

export function pointInBoundaryGeojson(pointGeojson, geojson) {
  const coords = pointCoords(pointGeojson);
  if (!coords || !geojson) return false;
  const [lng, lat] = coords;
  if (geojson.type === 'Polygon' && Array.isArray(geojson.coordinates?.[0])) {
    return ringContains(lng, lat, geojson.coordinates[0]);
  }
  if (geojson.type === 'MultiPolygon' && Array.isArray(geojson.coordinates)) {
    return geojson.coordinates.some((poly) => Array.isArray(poly?.[0]) && ringContains(lng, lat, poly[0]));
  }
  return false;
}

/** Containing tract wins; otherwise nearest tract centroid. */
export function parentHuntLocationToTract(pointGeojson, tracts) {
  const list = clubTracts(tracts);
  if (list.length === 0) return null;
  const containing = list.find((tract) => pointInBoundaryGeojson(pointGeojson, tract.geojson));
  if (containing) return containing;
  const coords = pointCoords(pointGeojson);
  if (!coords) return list[0];
  const [lng, lat] = coords;
  let nearest = null;
  let nearestMiles = Infinity;
  list.forEach((tract) => {
    const centroid = getCentroidFromPolygonOrMultiPolygon(tract.geojson);
    if (!centroid) return;
    const miles = calculateDistance(lat, lng, centroid.lat, centroid.lng);
    if (miles < nearestMiles) {
      nearestMiles = miles;
      nearest = tract;
    }
  });
  return nearest || list[0];
}

export function huntBoundaryCreatePayload({
  name,
  geojson,
  notes,
  clubMapId,
  isBoundary = true,
  lineColor,
  fillColor,
  fillOpacity,
}) {
  return {
    name: name || 'Unnamed Boundary',
    geojson,
    notes,
    club_map: clubMapId,
    is_boundary: isBoundary !== false,
    ...(lineColor ? { line_color: lineColor } : {}),
    ...(fillColor ? { fill_color: fillColor } : {}),
    ...(typeof fillOpacity === 'number' ? { fill_opacity: fillOpacity } : {}),
  };
}

/** Name parcel-selected clusters: one name, two West/East, else west-to-east N. */
export function clubTractNamesFromClusters(geometries, baseName = '') {
  const list = Array.isArray(geometries) ? geometries : [];
  if (list.length === 0) return [];
  const base = String(baseName || '').trim();
  if (list.length === 1) return [base || 'Boundary'];
  const ranked = list.map((geojson, index) => {
    const centroid = getCentroidFromPolygonOrMultiPolygon(geojson);
    return { index, lng: centroid && typeof centroid.lng === 'number' ? centroid.lng : 0 };
  }).sort((a, b) => a.lng - b.lng);
  const names = new Array(list.length);
  if (list.length === 2) {
    names[ranked[0].index] = base ? `${base} West` : 'West Tract';
    names[ranked[1].index] = base ? `${base} East` : 'East Tract';
    return names;
  }
  ranked.forEach((row, i) => {
    names[row.index] = base ? `${base} ${i + 1}` : `Tract ${i + 1}`;
  });
  return names;
}

export function huntBoundarySaveError(responseData, fallback = 'Could not save the club boundary.') {
  if (!responseData) return fallback;
  if (typeof responseData === 'string' && responseData.trim()) return responseData;
  if (responseData.error) return String(responseData.error);
  if (typeof responseData.detail === 'string' && responseData.detail.trim()) return responseData.detail;
  const firstKey = Object.keys(responseData).find((key) => responseData[key] != null);
  if (!firstKey) return fallback;
  const raw = responseData[firstKey];
  const msg = Array.isArray(raw) ? raw[0] : raw;
  if (msg == null || msg === '') return fallback;
  return firstKey === 'club_map' ? 'This club has no map record yet. Refresh and try again.' : String(msg);
}

export function huntBoundaryRoleError(responseData) {
  const msg = responseData?.is_boundary;
  if (Array.isArray(msg) && msg[0]) return String(msg[0]);
  if (typeof msg === 'string' && msg) return msg;
  return null;
}
